import { useCallback, useEffect, useState } from "react";
import { Message, Popconfirm, Spin, Switch, Table } from "@/frontend/components/common";
import type { ColumnsType } from "antd/es/table";
import { teamService, tournamentParticipantService } from "@/frontend/services";
import type { TeamResponseDTO } from "@/shared/dto";

interface TeamsRosterProps {
  tournamentId: string;
  seasonId: string;
}

interface TeamRosterRow {
  teamId: string;
  name: string;
  code: string;
  flag: string | null;
  inCurrentSeason: boolean;
  participantId: string | undefined;
}

export function TeamsRoster({ tournamentId, seasonId }: TeamsRosterProps) {
  const [rows, setRows] = useState<TeamRosterRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [togglingTeamId, setTogglingTeamId] = useState<string | null>(null);

  const fetchRoster = useCallback(async () => {
    setLoading(true);
    try {
      const participants = await tournamentParticipantService.getAll({ tournamentId });
      const teamIds = Array.from(
        new Set(participants.map((p) => p.teamId).filter((id): id is string => Boolean(id))),
      );

      const teams = await Promise.all(
        teamIds.map((teamId) => teamService.getById(teamId)),
      );
      const teamById = new Map<string, TeamResponseDTO>();
      teams.forEach((res) => {
        if (res.success && res.data) teamById.set(res.data.id, res.data);
      });

      const currentSeasonParticipantByTeamId = new Map(
        participants
          .filter((p) => p.seasonId === seasonId && p.teamId)
          .map((p) => [p.teamId as string, p]),
      );

      const nextRows = teamIds
        .map((teamId) => {
          const team = teamById.get(teamId);
          const participant = currentSeasonParticipantByTeamId.get(teamId);
          if (!team) return null;
          return {
            teamId,
            name: team.name,
            code: team.code,
            flag: team.country?.flag ?? null,
            inCurrentSeason: Boolean(participant),
            participantId: participant?.id,
          } satisfies TeamRosterRow;
        })
        .filter((row): row is TeamRosterRow => row !== null)
        .sort((a, b) => a.name.localeCompare(b.name));

      setRows(nextRows);
    } catch (err) {
      Message.error({
        content: err instanceof Error ? err.message : "No se pudieron cargar los equipos del torneo",
      });
    } finally {
      setLoading(false);
    }
  }, [tournamentId, seasonId]);

  useEffect(() => {
    fetchRoster();
  }, [fetchRoster]);

  const handleAdd = async (row: TeamRosterRow) => {
    setTogglingTeamId(row.teamId);
    try {
      const res = await tournamentParticipantService.create({
        tournamentId,
        seasonId,
        teamId: row.teamId,
      });
      if (res.success && res.data) {
        setRows((prev) =>
          prev.map((r) =>
            r.teamId === row.teamId
              ? { ...r, inCurrentSeason: true, participantId: res.data!.id }
              : r,
          ),
        );
        Message.success({ content: `${row.name} agregado a la temporada` });
      } else {
        Message.error({ content: res.error ?? "No se pudo agregar el equipo a la temporada" });
      }
    } catch (err) {
      Message.error({
        content: err instanceof Error ? err.message : "No se pudo agregar el equipo a la temporada",
      });
    } finally {
      setTogglingTeamId(null);
    }
  };

  const handleRemove = async (row: TeamRosterRow) => {
    if (!row.participantId) return;
    setTogglingTeamId(row.teamId);
    try {
      const res = await tournamentParticipantService.delete(row.participantId);
      if (res.success) {
        setRows((prev) =>
          prev.map((r) =>
            r.teamId === row.teamId
              ? { ...r, inCurrentSeason: false, participantId: undefined }
              : r,
          ),
        );
        Message.success({ content: `${row.name} quitado de la temporada` });
      } else {
        Message.error({ content: res.error ?? "No se pudo quitar el equipo de la temporada" });
      }
    } catch (err) {
      Message.error({
        content: err instanceof Error ? err.message : "No se pudo quitar el equipo de la temporada",
      });
    } finally {
      setTogglingTeamId(null);
    }
  };

  const columns: ColumnsType<TeamRosterRow> = [
    {
      title: "Equipo",
      key: "name",
      render: (_, row) => (
        <div className="flex items-center gap-2">
          {row.flag && <img src={row.flag} alt="" className="w-5 h-3.5 object-cover rounded" />}
          <span className="font-medium">{row.name}</span>
        </div>
      ),
    },
    {
      title: "Código",
      dataIndex: "code",
      key: "code",
      width: 120,
    },
    {
      title: "En la temporada actual",
      key: "inCurrentSeason",
      width: 180,
      align: "center",
      // Activar no necesita confirmación; desactivar sí, porque quita al
      // equipo de la temporada (delete físico, no soft-delete).
      render: (_, row) => (
        <div className="flex justify-center">
          {row.inCurrentSeason ? (
            <Popconfirm
              title="¿Quitar este equipo de la temporada actual?"
              onConfirm={() => handleRemove(row)}
              okText="Quitar"
              cancelText="Cancelar"
            >
              <Switch checked loading={togglingTeamId === row.teamId} />
            </Popconfirm>
          ) : (
            <Switch
              checked={false}
              loading={togglingTeamId === row.teamId}
              onChange={() => handleAdd(row)}
            />
          )}
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spin size="large" tip="Cargando equipos..." />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="text-gray-500 py-4">
        Este torneo no tiene equipos asociados todavía.
      </p>
    );
  }

  return (
    <Table<TeamRosterRow>
      columns={columns}
      dataSource={rows}
      rowKey="teamId"
      pagination={{ pageSize: 10, hideOnSinglePage: true }}
    />
  );
}
